import { useTheme } from '@mui/material';
import { SankeyChartProps } from '@mui/x-charts-pro';
import { GuestJourneyMovementSankey } from 'api/reports-service/guest-journey-reports.types';
import IncSankeyChart from 'components/charts/inc-sankey-chart/inc-sankey-chart.component';
import { useTranslation } from 'react-i18next';

export type MovementSankeyProps = {
  data?: GuestJourneyMovementSankey;
  loading?: boolean;
  /** Center column header (selected stage name or offer name). */
  centerLabel?: string;
  /**
   * Override the colors of *incoming* neutral flows (those into the center) —
   * both the edge (ribbon) and the source node bar. Offer Impact uses this so
   * its incoming "Redeemed" flows read as blue (light-blue edge, deeper-blue
   * node) rather than the ambiguous movement grey, matching the design. Outgoing
   * and non-neutral flows are unaffected; omitted on Stage Movement, which keeps
   * the default neutral grey.
   */
  incomingNeutralColors?: { edge: string; node: string };
  'data-testid'?: string;
};

const MovementSankey = ({
  data,
  loading = false,
  centerLabel,
  incomingNeutralColors,
  'data-testid': dataTestId = 'MovementSankey',
}: MovementSankeyProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const linkColorByDirection = {
    up: theme.palette.success.main,
    down: theme.palette.error.main,
    neutral: theme.palette.grey[400],
  } as const;

  const nodeColorByDirection = {
    up: theme.palette.success.dark,
    down: theme.palette.error.dark,
    neutral: theme.palette.grey[700],
  } as const;

  const series: SankeyChartProps['series'] =
    data?.nodes?.length && data?.links?.length
      ? {
          // `sort: 'fixed'` preserves the node/link order buildMovementSankey
          // laid down (green → grey → red, value desc) instead of MUI's
          // automatic crossing-minimization layout.
          nodeOptions: { sort: 'fixed' },
          linkOptions: { showValues: true, sort: 'fixed' },
          data: {
            nodes: data.nodes.map(n => {
              // Incoming nodes carry the `in:` id prefix (see buildMovementSankey);
              // Offer Impact recolors incoming neutral (redemption) node bars.
              const isIncoming = n.id.startsWith('in:');
              return {
                id: n.id,
                label: n.label,
                color:
                  n.id === data.centerId
                    ? theme.palette.primary.main
                    : isIncoming &&
                      n.direction === 'neutral' &&
                      incomingNeutralColors
                    ? incomingNeutralColors.node
                    : nodeColorByDirection[n.direction],
              };
            }),
            links: data.links.map(link => {
              // Incoming edges flow into the center; Offer Impact recolors its
              // neutral (redemption) incoming edges via incomingNeutralColors.
              const isIncoming = link.target === data.centerId;
              return {
                source: link.source,
                target: link.target,
                value: link.value,
                color:
                  isIncoming &&
                  link.direction === 'neutral' &&
                  incomingNeutralColors
                    ? incomingNeutralColors.edge
                    : linkColorByDirection[link.direction],
              };
            }),
          },
        }
      : undefined;

  return (
    <IncSankeyChart
      title={t('page.guestJourney.movement.title')}
      series={series}
      loading={loading}
      emptyMessage={t('page.guestJourney.movement.noData')}
      headers={{
        start: t('page.guestJourney.movement.incoming'),
        center: centerLabel,
        end: t('page.guestJourney.movement.outgoing'),
      }}
      data-testid={dataTestId}
    />
  );
};

export default MovementSankey;
